import { StandardError } from "@errors";
import { EJoinTypes, EQueryOperators, EQueryTypes, TComplexCondition, TMultipelCondition, TSimpleCondition } from "@types";

export abstract class Query {
	public queryType: EQueryTypes;
	public tableName: string;


	constructor(queryType: EQueryTypes, tableName: string) {
		this.queryType = queryType;
		this.tableName = tableName;
		return this;
	}

	abstract compileQuery(): { queryString: string, queryData: any };

	public getExecutionParams(): any {
		return this.compileQuery();
	}

	public addJoinTable(...args: any[]) {
		throw new StandardError("Unable to perform action for this type of query");
	}
	public addFieldToSelect(...args: any[]) {
		throw new StandardError("Unable to perform action for this type of query");
	}
	public addExpressionToSelect(...args: any[]) {
		throw new StandardError("Unable to perform action for this type of query");
	}
	public addFieldToFilter(...args: any[]) {
		throw new StandardError("Unable to perform action for this type of query");
	}
	public addGroupBy(...args: any[]) {
		throw new StandardError("Unable to perform action for this type of query");
	}
	public addDataToUpdate(...args: any[]) {
		throw new StandardError("Unable to perform action for this type of query");
	}
	public addDataToInsert(...args: any[]) {
		throw new StandardError("Unable to perform action for this type of query");
	}
	public addLimit(...args: any[]) {
		throw new StandardError("Unable to perform action for this type of query");
	}
	public addOrderBy(...args: any[]) {
		throw new StandardError("Unable to perform action for this type of query");
	}
}

export abstract class FilterableQuery extends Query {
	protected fieldFilters: any[] = [];
	public whereCompiledData: { string: string, data: any };

	static VALID_CONDITION_KEYS = ["eq", "neq", "like", "notlike", "in", "notin", "notnull", "null", "gt", "lt", "gteq", "lteq"];


	static isTMultipleCondition = (condition: any): condition is TMultipelCondition => Array.isArray(condition);

	static isTSimpleCondition = (condition: any): condition is TSimpleCondition => (
		Number.isFinite(condition) ||
		typeof condition === "string" ||
		condition instanceof Date ||
		condition === true ||
		condition === false
	);

	static isTComplexCondition = (condition: any): condition is TComplexCondition => (
		!FilterableQuery.isTSimpleCondition(condition) &&
		FilterableQuery.VALID_CONDITION_KEYS.includes(Object.keys(condition)[0]) != null
	);

	//#region Add sections to query

	public addFieldToFilter(field: string | string[], condition: TSimpleCondition | TComplexCondition | TMultipelCondition) {
		if (this.checkAddFieldToFilterData(field, condition))
			this.prepareFieldFilters(field, condition);
		return this;
	}

	//#endregion


	//#region Validate added regions of query

	protected checkAddFieldToFilterData(field: string | string[], condition: TSimpleCondition | TComplexCondition | TMultipelCondition) {
		if (!this.queryType)
			throw new StandardError(null, "Query type must be defined before adding fields to select");
		if (this.queryType == EQueryTypes.insert)
			throw new StandardError(null, `Adding filters is not allowed for ${this.queryType} queries`);

		// If fields is an array, it must have at least one value
		if (Array.isArray(field) && !(field.length > 0))
			throw new StandardError(null, "The array of fields failed to pass. The array must include at least one field.");

		// If field is not an array, it must not be empty
		if (!Array.isArray(field) && !field)
			throw new StandardError(null, "The array of fields failed to pass. The array must include at least one field.");

		// If multiple fields are given and multiple conditions are given, each field must match a condition 
		if (Array.isArray(field) && Array.isArray(condition) && field.length != condition.length)
			throw new StandardError(null, "The field array failed to pass. The field array must have a matching condition array.");

		// For complex conditions, check that they have a valid key
		// Check each complex condition for the ones given
		if (FilterableQuery.isTMultipleCondition(condition)) {
			if (!this.checkValidConditionArray(condition))
				throw new StandardError(null, "Some conditions contain invalid keys.");
		}
		else if (!this.checkValidCondition(condition))
			throw new StandardError(null, "The condition contains invalid keys.");
		return true;
	}

	protected checkValidConditionArray(condition: TMultipelCondition): boolean {
		return condition.find(con => this.checkValidCondition(con)) != null;
	}

	protected checkValidCondition(condition: TSimpleCondition | TComplexCondition): boolean {
		return ((!FilterableQuery.isTComplexCondition(condition) && FilterableQuery.isTSimpleCondition(condition)) || this.checkValidComplexCondition(condition));
	}

	protected checkValidComplexCondition(condition: TComplexCondition): boolean {
		if (!FilterableQuery.isTComplexCondition(condition)) return false;
		const [condKey, condValue] = Object.entries(condition)[0];
		switch (condKey) {
			case "eq":
			case "neq":
			case "like":
			case "notlike":
			case "gt":
			case "lt":
			case "gteq":
			case "lteq":
				return FilterableQuery.isTSimpleCondition(condValue);
			case "in":
			case "notin":
				return Array.isArray(condValue) && this.checkValidConditionArray(condValue);
			case "notnull":
			case "null":
				return typeof (condValue) === "boolean";
		}
		return false;
	}

	//#endregion


	//#region Prepare added sections

	/*
	this.fieldFilters = [
		QUERYFILTER: [
			LINEFILTER: {
				FIELDNAME: FIELDCONDITIONS: [
					CONDITION
					OR
					CONDITION
				]
				OR
				FIELDNAME: FIELDCONDITIONS
			}
		]
		AND
		QUERYFILTER
	]
	each entry in fieldFilters is a QUERYFILTER
	QUERYFILTER has to be an array of LINEFILTERS

	LINEFILTERS must have the type {FIELDNAME: FIELDCONDITIONS}
	FIELDNAME must be a string
	FIELDCONDITIONS has to be an array

	each entry of FIELDCONDITIONS is a CONDITION
	a CONDITION has to be a value or an object
	
	each QUERYFILTER is added with an AND operator for the query
	each key/value pair of LINEFILTERS is added with an OR operator for the QUERYFILTER
	each CONDITIONS is added with an OR operator for the FIELDNAME  it represents

	each call to addFieltToFilter adds a QUERYFILTER
	*/
	protected prepareFieldFilters(field: string | string[], condition: TSimpleCondition | TComplexCondition | TMultipelCondition) {
		const newFilter: { [fieldName: string]: TMultipelCondition } = {};

		// Single field, single condition
		if (!Array.isArray(field) && !FilterableQuery.isTMultipleCondition(condition)) {
			if (FilterableQuery.isTSimpleCondition(condition))
				// Condition is a value
				newFilter[field] = [{ eq: condition }];
			else
				// Condition is complex
				newFilter[field] = [condition];
			this.fieldFilters.push(newFilter);
		}

		// Single field, multiple conditions
		if (!Array.isArray(field) && FilterableQuery.isTMultipleCondition(condition)) {
			newFilter[field] = condition.map((cond: TSimpleCondition | TComplexCondition): TSimpleCondition | TComplexCondition => {
				if (FilterableQuery.isTSimpleCondition(cond))
					// Condition is a value
					return { eq: cond };
				else
					// Condition is complex
					return cond;
			});
			this.fieldFilters.push(newFilter);
		}

		// Multiple fields, single condition
		if (Array.isArray(field) && !FilterableQuery.isTMultipleCondition(condition)) {
			const newFilter: { [fieldName: string]: TMultipelCondition } =
				field.reduce((carry: { [fieldName: string]: TMultipelCondition }, fieldName: string) => {
					if (FilterableQuery.isTSimpleCondition(condition))
						// Condition is a value
						carry[fieldName] = [{ eq: condition }];
					else
						// Condition is complex
						carry[fieldName] = [condition];
					return carry;
				}, {});
			this.fieldFilters.push(newFilter);
		}

		// Multiple fields, multiple conditions
		if (Array.isArray(field) && FilterableQuery.isTMultipleCondition(condition)) {
			const newFilter: { [fieldName: string]: TMultipelCondition } = {};
			for (let fieldCounter = 0; fieldCounter < field.length; fieldCounter++) {
				const fieldName = field[fieldCounter];
				const cond = condition[fieldCounter];
				newFilter[fieldName] = [FilterableQuery.isTSimpleCondition(cond) ? { eq: cond } : cond];

			}
			this.fieldFilters.push(newFilter);
		}

		return this;
	}

	//#endregion


	//#region Compile added sections

	public compileWhereData(): any {
		if (!this.fieldFilters.length)
			return { string: "", data: [] };
		const { string, data } = this.compileQueryFilter(this.fieldFilters);
		this.whereCompiledData = { string, data };
		return this.whereCompiledData;
	}

	protected compileQueryFilter(queryFilters: any[]): any {
		const formattedLineFilters = queryFilters.map(lineFilter => this.compileLineFilter(lineFilter));

		const strings = formattedLineFilters.map(({ string }) => string);
		const datas = formattedLineFilters.reduce((carry: any[], { data }) => [...carry, ...data], []);

		return { string: "(" + strings.join(" AND ") + ")", data: datas };
	}

	protected compileLineFilter(lineFilters: { [fieldName: string]: any }): any {
		const formattedFieldConditions = Object.entries(lineFilters).map(([fieldName, conditions]) => this.compileFieldConditions(fieldName, conditions));

		const strings = formattedFieldConditions.map(({ string }) => string);
		const datas = formattedFieldConditions.reduce((carry: any[], { data }) => [...carry, ...data], []);

		return { string: "(" + strings.join(" OR ") + ")", data: datas };
	}

	protected compileFieldConditions(fieldName: string, conditions: (TSimpleCondition | TComplexCondition)[]): any {
		const formattedConditions = conditions.map(condition => this.compileConditions(fieldName, condition));

		const strings = formattedConditions.map(({ string }) => string);
		const datas = formattedConditions.reduce((carry: any[], { data }) => [...carry, ...data], []);

		return { string: "(" + strings.join(" OR ") + ")", data: datas };
	}

	protected compileConditions(fieldName: string, condition: TSimpleCondition | TComplexCondition) {
		let operator: string;
		let fieldConditionsSegment: any;
		let fieldConditionsData: any[] = [];


		const [condKey, condValue] = Object.entries(condition)[0];
		switch (condKey) {
			case "eq":
			case "neq":
			case "like":
			case "notlike":
			case "gt":
			case "lt":
			case "gteq":
			case "lteq":
				operator = EQueryOperators[condKey];
				if (fieldName.includes("__")) {
					fieldConditionsSegment = `?? ${operator} ?`;
					fieldConditionsData = [...fieldConditionsData, fieldName, condValue];
				}
				else {
					fieldConditionsSegment = `??.?? ${operator} ?`;
					fieldConditionsData = [...fieldConditionsData, this.tableName, fieldName, condValue];
				}
				break;
			case "in":
			case "notin":
				operator = EQueryOperators[condKey];
				if (fieldName.includes("__")) {
					fieldConditionsSegment = `?? ${operator} (?)`;
					fieldConditionsData = [...fieldConditionsData, fieldName, condValue];
				}
				else {
					fieldConditionsSegment = `??.?? ${operator} (?)`;
					fieldConditionsData = [...fieldConditionsData, this.tableName, fieldName, condValue];
				}
				break;
			case "notnull":
				operator = condValue ? EQueryOperators[condKey] : EQueryOperators["null"];
				if (fieldName.includes("__")) {
					fieldConditionsSegment = `?? ${operator}`;
					fieldConditionsData = [...fieldConditionsData, fieldName];
				}
				else {
					fieldConditionsSegment = `??.?? ${operator}`;
					fieldConditionsData = [...fieldConditionsData, this.tableName, fieldName];
				}
				break;
			case "null":
				operator = condValue ? EQueryOperators[condKey] : EQueryOperators["notnull"];
				if (fieldName.includes("__")) {
					fieldConditionsSegment = `?? ${operator}`;
					fieldConditionsData = [...fieldConditionsData, fieldName];
				}
				else {
					fieldConditionsSegment = `??.?? ${operator}`;
					fieldConditionsData = [...fieldConditionsData, this.tableName, fieldName];
				}
				break;
		}
		return { string: fieldConditionsSegment, data: fieldConditionsData };
	}

	//#endregion
}

export class SelectQuery extends FilterableQuery {
	// Compiled sections
	public selectFieldsCompiledData: { string: string, data: any };
	public joinCompiledData: { string: string, data: any };
	public groupByCompiledData: { string: string, data: any };
	public limitCompiledData: { string: string, data: any };
	public orderByCompiledData: { string: string, data: any };

	// Added sections
	private selectFields: any[] = [];
	private selectExpressions: any[] = [];
	private joins: any[] = [];
	private groupBy: string[] = [];
	private limit: { start: number | null, size: number } | null;
	private orderBy: { field: string, type: string }[] = [];
	private nestTables: boolean | string = "__";

	constructor(tableName: string) {
		super(EQueryTypes.select, tableName);
		return this;
	}

	public compileQuery(): { queryString: string, queryData: any } {
		const { string: selectFieldsString, data: selectFieldsData } = this.compileSelectFieldsData();
		const { string: whereString, data: whereData } = this.compileWhereData();
		const { string: joinString, data: joinData } = this.compileJoinData();
		const { string: groupByString, data: groupByData } = this.compileGroupByData();
		const { string: limitString, data: limitData } = this.compileLimitData();
		const { string: orderByString, data: orderByData } = this.compileOrderByData();

		const queryStringSegments = [`SELECT ${selectFieldsString}`, "FROM ??"];
		let queryData = [...selectFieldsData, this.tableName];

		if (joinString && joinData.length) {
			queryStringSegments.push(`${joinString.trim()}`);
			queryData = [...queryData, ...joinData];
		}

		if (whereString && whereData.length) {
			queryStringSegments.push(`WHERE ${whereString.trim()}`);
			queryData = [...queryData, ...whereData];
		}

		if (groupByString && groupByData.length) {
			queryStringSegments.push(`GROUP BY ${groupByString.trim()}`);
			queryData = [...queryData, ...groupByData];
		}

		if (orderByString && orderByData.length) {
			queryStringSegments.push(`ORDER BY ${orderByString.trim()}`);
			queryData = [...queryData, ...orderByData];
		}

		if (limitString && limitData.length) {
			queryStringSegments.push(`LIMIT ${limitString.trim()}`);
			queryData = [...queryData, ...limitData];
		}

		const queryString = queryStringSegments.join(" ");

		return { queryString, queryData };
	}

	public getExecutionParams(): any {
		return {...this.compileQuery(), additionalParams: { nestTables: this.nestTables }};
	}

	//#region Add sections to query

	public addFieldToSelect(field: string | string[] | { [key: string]: string } | { [key: string]: string }[]) {
		if (this.checkAddFieldToSelect(field))
			this.prepareSelectFields(field);
		return this;
	}

	public addExpresionToSelect(expresionData: { [key: string]: string }) {
		if (this.checkAddExpressionToSelect(expresionData))
			this.prepareSelectExpressions(expresionData);
		return this;
	}

	public addJoinTable(tableName: string | { [key: string]: string }, fields: "*" | { [key: string]: string }, condition: string, type?: "inner" | "left" | "right" | "outer") {
		if (this.checkJoinTable(tableName, fields, condition, type))
			this.prepareJoinTable(tableName, fields, condition, type);

		return this;
	}

	public addGroupBy(field: string | string[]) {
		if (this.checkAddGroupBy(field))
			this.prepareGroupBy(field);
		return this;
	}

	public addLimit(limit1: number, limit2?: number) {
		if (this.checkAddLimit(limit1, limit2))
			this.prepareLimit(limit1, limit2);
		return this;
	}

	public addOrderBy(orderField: string, orderType?: "ASC" | "DESC") {
		if (this.checkAddOrderBy(orderField, orderType))
			this.prepareOrderBy(orderField, orderType);
		return this;
	}

	//#endregion

	//#region Validate added regions of query

	private checkAddFieldToSelect(field: string | string[] | { [key: string]: string } | { [key: string]: string }[]) {
		if (!this.queryType)
			throw new StandardError(null, "Query type must be defined before adding fields to select");
		if (this.queryType != EQueryTypes.select)
			throw new StandardError(null, `Adding fields to select is not allowed for ${this.queryType} queries`);
		return true;
	}

	private checkAddExpressionToSelect(field: { [key: string]: string }) {
		if (!this.queryType)
			throw new StandardError(null, "Query type must be defined before adding expressions to select");
		if (this.queryType != EQueryTypes.select)
			throw new StandardError(null, `Adding expressions to select is not allowed for ${this.queryType} queries`);
		return true;
	}

	private checkJoinTable(tableName: string | { [key: string]: string }, fields: "*" | { [key: string]: string }, condition: string, type?: "inner" | "left" | "right" | "outer") {
		if (!this.queryType)
			throw new StandardError(null, "Query type must be defined before adding fields to select");
		if (this.queryType != EQueryTypes.select)
			throw new StandardError(null, `Adding joined tables is not allowed for ${this.queryType} queries`);

		if (type && !Object.keys(EJoinTypes).includes(type))
			throw new StandardError(null, "Invalid join type.");
		return true;
	}

	private checkAddGroupBy(field: string | string[]) {
		if (!this.queryType)
			throw new StandardError(null, "Query type must be defined before adding fields to select");
		if (this.queryType != EQueryTypes.select)
			throw new StandardError(null, `Adding fields to group by is not allowed for ${this.queryType} queries`);
		return true;
	}

	private checkAddLimit(limit1: number, limit2?: number) {
		if (!this.queryType)
			throw new StandardError(null, "Query type must be defined before adding fields to select");
		if (this.queryType != EQueryTypes.select)
			throw new StandardError(null, `Adding limit is not allowed for ${this.queryType} queries`);
		if (limit1 < 0)
			throw new StandardError(null, `Lower limit (${limit1}) must be greater than 0`);
		return true;
	}

	private checkAddOrderBy(orderField: string, orderType?: "ASC" | "DESC") {
		if (!this.queryType)
			throw new StandardError(null, "Query type must be defined before adding fields to select");
		if (this.queryType != EQueryTypes.select)
			throw new StandardError(null, `Adding fields to insert is not allowed for ${this.queryType} queries`);
		return true;
	}

	//#endregion

	//#region Prepare added sections

	private prepareSelectFields(field: string | string[] | { [key: string]: string } | { [key: string]: string }[]) {
		if (Array.isArray(field)) {
			const newFields = field.map((fieldData: string | { [key: string]: string }) => {
				if (typeof (fieldData) === "string")
					return { field: fieldData };
				const [fieldName, alias] = Object.entries(fieldData)[0];
				return { field: fieldName, alias };
			});
			this.selectFields = [...this.selectFields, ...newFields];
		}
		else if (typeof (field) === "string")
			this.selectFields.push({ field: field });
		else {
			const [fieldName, alias] = Object.entries(field)[0];
			this.selectFields.push({ field: fieldName, alias });
		}
	}

	private prepareSelectExpressions(expressionData: { [key: string]: string }) {
		const [alias, expression] = Object.entries(expressionData)[0];
		this.selectExpressions.push({ expression: expression, alias });
	}

	private prepareJoinTable(tableName: string | { [key: string]: string }, fields: "*" | { [key: string]: string }, condition: string, type?: "inner" | "left" | "right" | "outer") {
		this.joins.push({
			type,
			fields,
			tableName,
			condition
		});
		return this;
	}

	private prepareGroupBy(field: string | string[]) {
		if (Array.isArray(field))
			this.groupBy = [...this.groupBy, ...field];
		else
			this.groupBy = [...this.groupBy, field];
		return this;
	}

	private prepareLimit(limitFrom: number, limitSize?: number) {
		const start = limitSize ? limitFrom : null;
		const size = limitSize ?? limitFrom;
		this.limit = { start, size };
		return this;
	}

	private prepareOrderBy(orderField: string, orderType?: "ASC" | "DESC") {
		this.orderBy.push({ field: orderField, type: orderType || "ASC" });
		return this;
	}

	//#endregion

	//#region Compile added sections

	protected compileSelectFieldsData() {
		// Add join fields to select, if none of the require a nested join
		// this.nestTables = true;
		// if (this.joins.length) {
		// 	this.nestTables = this.joins.some(({ fields }) => fields == "*");
		// 	if (!this.nestTables)
		// 		for (const joinData of this.joins)
		// 			this.addFieldToSelect(joinData.fields);
		// }

		if (!this.selectFields.length && !this.selectExpressions.length)
			return { string: "*", data: [] };

		const selectDataFields = this.selectFields.length ? this.selectFields.map(({ field, alias }) => {
			const string = alias ? "?? as ??" : "??";
			const data = alias ? [field, alias] : [field];
			return { string, data };
		}) : [{ string: "*", data: [] }];

		const selectDataExpressions = this.selectExpressions.map(({ expression, alias }) => {
			const string = `${expression} as ?`;
			return { string, data: [alias] };
		});

		const selectFieldsData = [...selectDataFields, ...selectDataExpressions];


		const strings = selectFieldsData.map(({ string }) => string);
		const datas = selectFieldsData.reduce((carry: any[], { data }) => [...carry, ...data], []);

		this.selectFieldsCompiledData = { string: strings.join(", "), data: datas };
		return this.selectFieldsCompiledData;
	}

	protected compileJoinData() {
		if (!this.joins.length)
			return { string: "", data: [] };
		const joinData = this.joins.map(({ type, tableName, condition }: { type: "inner" | "left" | "right" | "outer", tableName: string | { [key: string]: string }, condition: string }) => {
			let string;
			const data = [];
			const joinOperator: string = type ? EJoinTypes[type] : "JOIN";
			if (typeof (tableName) === "string") {
				string = `${joinOperator} ?? ON ${condition}`;
				data.push(tableName);
			}
			else {
				const [realTableName, tableAlias] = Object.entries(tableName)[0];
				string = `${joinOperator} ?? as ?? ON ${condition}`;
				data.push(realTableName, tableAlias);
			}
			return { string, data };
		});

		const strings = joinData.map(({ string }) => string);
		const datas = joinData.reduce((carry: any[], { data }) => [...carry, ...data], []);

		this.joinCompiledData = { string: strings.join(" "), data: datas };
		return this.joinCompiledData;
	}

	protected compileGroupByData() {
		if (!this.groupBy.length)
			return { string: "", data: [] };
		const groupByData = this.groupBy.map(field => {
			const string = field.includes(".") ? "??.??" : "??";
			const data = field.includes(".") ? field.split(".") : field;
			return { string, data };
		});

		const strings = groupByData.map(({ string }) => string);
		const datas = groupByData.reduce((carry: any[], { data }) => [...carry, ...data], []);

		this.groupByCompiledData = { string: strings.join(", "), data: datas };
		return this.groupByCompiledData;
	}

	protected compileLimitData() {
		if (!this.limit)
			return { string: "", data: [] };

		let string, data;
		if (this.limit.start) {
			string = "?, ?";
			data = [this.limit.start, this.limit.size];
		}
		else {
			string = "?";
			data = [this.limit.size];
		}

		this.limitCompiledData = { string, data };
		return this.limitCompiledData;
	}

	protected compileOrderByData() {
		if (!this.orderBy.length)
			return { string: "", data: [] };

		const [strings, data] = this.orderBy.reduce(([carryString, carryData]: string[][], { field, type }) => {
			carryString.push(`?? ${type}`);
			carryData.push(field);
			return [carryString, carryData];
		}, [[], []]);

		this.orderByCompiledData = { string: strings.join(", "), data: data };
		return this.orderByCompiledData;
	}

	//#endregion
}

export class InsertQuery extends Query {
	// Compiled sections
	public insertValuesCompiledData: { string: string, data: any };
	public insertFieldsCompiledData: { string: string, data: any };

	// Added sections
	private insertData: any[] = [];

	constructor(tableName: string) {
		super(EQueryTypes.insert, tableName);
		return this;
	}

	public compileQuery(): { queryString: string, queryData: any } {
		const { string: insertFieldsString, data: insertFieldsData } = this.compileInsertFieldsData();
		const { string: insertValuesString, data: insertValuesData } = this.compileInsertValuesData();

		const queryStringSegments = ["INSERT INTO ??"];
		let queryData = [this.tableName];

		if (insertFieldsString && insertFieldsData.length) {
			queryStringSegments.push(`(${insertFieldsString.trim()})`);
			queryData = [...queryData, ...insertFieldsData];
		}

		if (insertValuesString && insertValuesData.length) {
			queryStringSegments.push("VALUES");
			queryStringSegments.push(`${insertValuesString.trim()}`);
			queryData = [...queryData, ...insertValuesData];
		}

		const queryString = queryStringSegments.join(" ");

		return { queryString, queryData };
	}

	//#region Add sections to query

	public addDataToInsert(insertData: { [key: string]: any }) {
		if (this.checkAddDataToInsert(insertData))
			this.prepareDataToInsert(insertData);
		return this;
	}

	//#endregion

	//#region Validate added regions of query

	private checkAddDataToInsert(insertData: { [key: string]: string }) {
		if (!this.queryType)
			throw new StandardError(null, "Query type must be defined before adding fields to select");
		if (this.queryType != EQueryTypes.insert)
			throw new StandardError(null, `Adding fields to insert is not allowed for ${this.queryType} queries`);
		return true;
	}

	//#endregion

	//#region Prepare added sections

	private prepareDataToInsert(insertData: { [key: string]: any }) {
		this.insertData.push(insertData);
		return this;
	}

	//#endregion

	//#region Compile added sections

	protected compileInsertFieldsData() {
		if (!this.insertData.length)
			throw new StandardError(null, "No data to insert");

		const uniqueFieldNames = this.insertData.reduce((carry, insertedModel) => [...carry, ...Object.keys(insertedModel)], []).unique();

		const insertFieldsData = uniqueFieldNames.map((fieldName: string) => {
			const string = "??";
			const data = fieldName;
			return { string, data };
		});

		const strings = insertFieldsData.map(({ string }: { string: string }) => string);
		const datas = insertFieldsData.reduce((carry: any[], { data }: { data: any }) => [...carry, data], []);

		this.insertFieldsCompiledData = { string: strings.join(", "), data: datas };
		return this.insertFieldsCompiledData;
	}

	protected compileInsertValuesData() {
		if (!this.insertData.length)
			throw new StandardError(null, "No data to insert");

		const uniqueFieldNames = this.insertData.reduce((carry, insertedModel) => [...carry, ...Object.keys(insertedModel)], []).unique();

		const insertFieldsData = this.insertData.map((insertedModel) => {

			const { string, data } = uniqueFieldNames.reduce(({ string, data }: { string: string[], data: any[] }, fieldName: string) => {
				string.push("?");
				data.push(insertedModel[fieldName] ?? null);
				return { string, data };
			}, { string: [], data: [] });

			return { string: "(" + string + ")", data };
		});

		const strings = insertFieldsData.map(({ string }: { string: string }) => string);
		const datas = insertFieldsData.reduce((carry: any[], { data }: { data: any }) => [...carry, ...data], []);

		this.insertValuesCompiledData = { string: strings.join(", "), data: datas };
		return this.insertValuesCompiledData;
	}

	//#endregion
}

export class UpdateQuery extends FilterableQuery {
	// Compiled sections
	public updateFieldsCompiledData: { string: string, data: any };

	// Added sections
	private updateData: any[] = [];


	constructor(tableName: string) {
		super(EQueryTypes.update, tableName);
		return this;
	}

	public compileQuery(): { queryString: string, queryData: any } {
		const { string: updateString, data: updateData } = this.compileUpdateFieldsData();
		const { string: filterString, data: filterData } = this.compileWhereData();

		const queryStringSegments = ["UPDATE ??"];
		let queryData = [this.tableName];

		if (updateString && updateData.length) {
			queryStringSegments.push(`SET ${updateString.trim()}`);
			queryData = [...queryData, ...updateData];
		}

		if (filterString && filterData.length) {
			queryStringSegments.push(`WHERE ${filterString.trim()}`);
			queryData = [...queryData, ...filterData];
		}

		const queryString = queryStringSegments.join(" ");

		return { queryString, queryData };
	}

	//#region Add sections to query

	public addDataToUpdate(updateData: { [key: string]: any }) {
		if (this.checkAddDataToUpdate(updateData))
			this.prepareDataToUpdate(updateData);
		return this;
	}

	//#endregion

	//#region Validate added regions of query

	private checkAddDataToUpdate(updateData: { [key: string]: string }) {
		if (!this.queryType)
			throw new StandardError(null, "Query type must be defined before adding fields to select");
		if (this.queryType != EQueryTypes.update)
			throw new StandardError(null, `Adding fields to update is not allowed for ${this.queryType} queries`);
		return true;
	}

	//#endregion

	//#region Prepare added sections

	private prepareDataToUpdate(updateData: { [key: string]: any }) {
		this.updateData.push(updateData);
		return this;
	}

	//#endregion

	//#region Compile added sections

	protected compileUpdateFieldsData() {
		if (!this.updateData.length)
			throw new StandardError(null, "No data to update");

		const updateData = this.updateData.map((updatedModel) => {
			const string = "?";
			const data = updatedModel;
			return { string, data };
		});

		const strings = updateData.map(({ string }) => string);
		const datas = updateData.reduce((carry: any[], { data }) => [...carry, data], []);

		this.updateFieldsCompiledData = { string: strings.join(", "), data: datas };
		return this.updateFieldsCompiledData;
	}

	//#endregion
}

export class DeleteQuery extends FilterableQuery {
	constructor(tableName: string) {
		super(EQueryTypes.delete, tableName);
		return this;
	}

	public compileQuery(): { queryString: string, queryData: any } {
		const { string: filterString, data: filterData } = this.compileWhereData();

		const queryStringSegments = ["DELETE FROM ??"];
		let queryData = [this.tableName];

		if (filterString && filterData.length) {
			queryStringSegments.push(`WHERE ${filterString.trim()}`);
			queryData = [...queryData, ...filterData];
		}

		const queryString = queryStringSegments.join(" ");

		return { queryString, queryData };
	}

	//#region Compile added sections
	//#endregion
}